import { CloseOutlined, ThunderboltFilled } from "@ant-design/icons"
import { Empty, Table } from "antd"
import { ArrowLeft } from "lucide-react"
import { useTranslation } from "react-i18next"

import emptyIconDark from "~/assets/empty-state-dark.svg"
import emptyIconLight from "~/assets/empty-state-light.svg"
import { OSLoader } from "~/components/base-loader"
import useGlobalStore from "~/store/global"
import { track } from "~/utils/amplitude"
import { getBaseEventProps } from "~/utils/amplitude-events"

import { EVENTS } from "../knowledge-os/pre-meeting/constants"

type PeopleResultsProps = {
  peopleOptions: any[]
  fetching: boolean
  columns: any[]
  rowSelection: any
  currentPage: number
  setCurrentPage: (n: number) => void
  formData: any
  people: any[]
  selectedPeople: any[]
  setSelectedPeople: (rows: any[]) => void
  handleGenerateBriefButtonClick: () => void
  searchLoading: boolean
  searched: boolean
  setSearched?: (value: boolean) => void
  pendingRequests: number
}

const PeopleResults = ({
  peopleOptions,
  fetching,
  columns,
  rowSelection,
  currentPage,
  setCurrentPage,
  formData,
  people,
  selectedPeople,
  setSelectedPeople,
  handleGenerateBriefButtonClick,
  searchLoading,
  searched,
  setSearched,
  pendingRequests
}: PeopleResultsProps) => {
  const { t } = useTranslation("common")
  const appTheme = useGlobalStore((store) => store.appTheme)
  const isDarkMode = appTheme === "dark"
  const feature_flags = useGlobalStore((store) => store.user?.feature_flags)
  const showNewFlow = feature_flags?.show_upcoming_meeting_brief

  return peopleOptions?.length > 0 ? (
    <div className="flex flex-col flex-1 w-full overflow-auto">
      {showNewFlow && (
        <div className="flex justify-left p-2 gap-2 items-center">
          <button
            onClick={() => setSearched && setSearched(false)}
            className="flex gap-2 flex-row items-center"
          >
            <ArrowLeft className="w-4 h-4 text-dark-2 dark:text-dark-6" />
            <p className="text-md text-dark-2 dark:text-dark-6">
              {" "}
              Search results
            </p>
          </button>
        </div>
      )}
      <Table
        rowSelection={rowSelection}
        loading={fetching}
        columns={columns}
        dataSource={peopleOptions}
        bordered
        size="small"
        pagination={{
          pageSize: 12,
          showSizeChanger: false,
          current: currentPage,
          onChange: (page, pageSize) => {
            setCurrentPage(page)
            track(EVENTS.PAGINATION_CLICKED, {
              ...getBaseEventProps({
                feature: "pre_meeting_brief",
                accountId: formData?.id,
                accountName: formData?.name
              }),
              timestamp: Date.now(),
              page: page,
              size: pageSize,
              account_selected: formData?.name
            })
          }
        }}
        onRow={(record) => ({
          onClick: () => rowSelection.onRowClick(record),
          style: { cursor: "pointer" }
        })}
      />
      {people.length == 0 ? (
        <Empty description={t("pre_meeting_brief.empty_no_results_found")} />
      ) : null}
      <div
        className={`flex items-center justify-between mx-auto rounded-full bg-primary text-white min-w-[340px] ${
          selectedPeople.length === 0 ? "opacity-50 cursor-not-allowed" : ""
        }`}
      >
        <button
          onClick={() => {
            setSelectedPeople([])
          }}
          className="text-xs font-normal flex items-center gap-2 text-center px-4 h-10"
          disabled={selectedPeople.length === 0}
        >
          <CloseOutlined />
          {t("pre_meeting_brief.label_people_selected", {
            count: selectedPeople.length
          })}
        </button>
        <div className="w-0.5 bg-gray-300 h-6"></div>
        <button
          onClick={handleGenerateBriefButtonClick}
          className="text-xs font-normal flex items-center gap-2 text-center px-4 h-10"
          disabled={selectedPeople.length === 0}
        >
          <ThunderboltFilled />{" "}
          {t("pre_meeting_brief.cta_view_pre_meeting_brief")}
        </button>
      </div>
    </div>
  ) : (searchLoading && !searched && true) || pendingRequests > 0 ? (
    <div className="flex justify-center items-center flex-col gap-4 mt-20 w-full">
      <OSLoader />
      <p className="text-13 font-semibold dark:text-dark-6 text-dark-2">
        {t("pre_meeting_brief.label_results_generation_time_info")}
      </p>
    </div>
  ) : people?.length === 0 && searched && !searchLoading ? (
    <Empty
      description={t("pre_meeting_brief.empty_no_results_found")}
      className="mt-6 justify-center items-center w-full"
    />
  ) : (
    <div className="flex justify-center items-center flex-col gap-4 mt-20 w-full">
      <div className="p-4 rounded-lg">
        <img
          key={"empty-state-icon"}
          src={isDarkMode ? emptyIconDark : emptyIconLight}
          width={167}
          height={100}
          alt="empty-state-icon"
          className="text-xl text-neutral-50 dark:text-neutral-800"
        />
      </div>
      <p className="text-13 font-semibold text-dark-2 dark:text-dark-6">
        {t(
          "pre_meeting_brief.label_choose_account_and_search_people_to_view_pre_meeting_brief"
        )}
      </p>
    </div>
  )
}

export default PeopleResults
